// --- Dependencies -------------------------------------------------------------------------------
import {
    ReactQRCode,
    type DataModulesStyle,
    type DownloadFileFormat,
    type FinderPatternInnerStyle,
    type FinderPatternOuterStyle,
    type ReactQRCodeRef,
} from "@lglab/react-qr-code";
import { useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyBranch } from "@/types";
import { buildMenuUrl, getMenuHost } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
};

/** A menu a code can point at. `branchSlug: null` is the store's default menu. */
type TyTarget = {
    id: string;
    label: string;
    branchSlug: string | null;
};

type TyStylePreset = {
    id: string;
    label: string;
    dataModules: DataModulesStyle;
    finderOuter: FinderPatternOuterStyle;
    finderInner: FinderPatternInnerStyle;
};

// --- Constants ----------------------------------------------------------------------------------

const STORE_TARGET_ID = "store";

/**
 * Always black on white. A menu code gets printed once and then lives on a table under bad light,
 * so contrast beats decoration — the styles below only change module shapes, never the colours.
 */
const QR_FOREGROUND = "#000000";
const QR_BACKGROUND = "#FFFFFF";

const PREVIEW_SIZE = 210;
const SWATCH_SIZE = 34;

/** The spec-mandated 4-module quiet zone. Without it, scanners hunting the edges give up. */
const QUIET_ZONE_MODULES = 4;

/** Raster exports are meant for print: 2048px keeps a 5cm sticker sharp at 300dpi. */
const EXPORT_SIZE = 2048;

/** A short constant so every swatch draws the smallest possible code — the shape stays readable. */
const SWATCH_VALUE = "MENUKET";

const DOWNLOAD_FORMATS: { id: DownloadFileFormat; label: string }[] = [
    { id: "svg", label: "SVG" },
    { id: "png", label: "PNG" },
    { id: "jpeg", label: "JPG" },
];

// --- Logic --------------------------------------------------------------------------------------

export default function QrCodesPopup({ onClose }: Props) {
    const t = useTranslation();
    const store = useStore((state) => state.store);
    const { canView, canViewBranch } = usePermission();

    const stylePresets: TyStylePreset[] = [
        { id: "classic", label: t.qrCodes.styleClassic, dataModules: "square", finderOuter: "square", finderInner: "square" },
        { id: "rounded", label: t.qrCodes.styleRounded, dataModules: "rounded", finderOuter: "rounded", finderInner: "rounded" },
        { id: "dots", label: t.qrCodes.styleDots, dataModules: "circle", finderOuter: "circle", finderInner: "circle" },
        { id: "leaf", label: t.qrCodes.styleLeaf, dataModules: "leaf", finderOuter: "leaf", finderInner: "leaf" },
    ];

    const qrRef = useRef<ReactQRCodeRef>(null);
    const copyTimerRef = useRef<number | null>(null);

    const [targetId, setTargetId] = useState(STORE_TARGET_ID);
    const [presetId, setPresetId] = useState(stylePresets[0].id);
    const [copied, setCopied] = useState(false);

    // The branch list is a `branches` capability, and inside it a client only gets codes for the
    // branches they were actually scoped to — the same scope the profile screen reports.
    const canSeeBranches = canView("branches");
    const { data, loading } = useFetch<TyApiResponse<{ branches: TyBranch[] }>>("/branches", {}, canSeeBranches);
    const branches = (data?.data?.branches ?? []).filter((branch) => canViewBranch(branch._id));

    const targets: TyTarget[] = [
        { id: STORE_TARGET_ID, label: t.qrCodes.storeTarget, branchSlug: null },
        ...branches.map((branch) => ({ id: branch._id, label: branch.name, branchSlug: branch.slug })),
    ];

    // A branch can vanish under us (deleted elsewhere, or the fetch resolving late), so never trust
    // the stored id without a lookup.
    const target = targets.find((item) => item.id === targetId) ?? targets[0];
    const preset = stylePresets.find((item) => item.id === presetId) ?? stylePresets[0];

    const slug = store?.slug ?? "";
    const url = buildMenuUrl(slug, target.branchSlug);
    const displayUrl = target.branchSlug ? `${getMenuHost(slug)}?branch=${target.branchSlug}` : getMenuHost(slug);
    const fileName = target.branchSlug ? `${slug}-${target.branchSlug}` : `${slug}-menu`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
            copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard access is blocked outside secure contexts; the link stays selectable instead.
        }
    };

    const handleDownload = (format: DownloadFileFormat) => {
        qrRef.current?.download({ name: fileName, format, size: EXPORT_SIZE });
    };

    return (
        <div className="qr-codes-popup">
            {/* --- Header ------------------------------------------------------------------- */}
            <header className="qcp-header">
                <div className="qcp-heading">
                    <p className="qcp-title">{t.qrCodes.title}</p>
                    <p className="qcp-subtitle">{t.qrCodes.subtitle}</p>
                </div>
                <button type="button" className="qcp-close" onClick={onClose} aria-label={t.qrCodes.closeAction}>
                    <img src="/icons/close-icon.svg" alt="" width={13} height={13} />
                </button>
            </header>

            {!slug ? (
                <div className="qcp-body">
                    <p className="qcp-empty">{t.qrCodes.noStore}</p>
                </div>
            ) : (
                <>
                    <div className="qcp-body">
                        {/* --- Which menu ------------------------------------------------------- */}
                        {canSeeBranches && (
                            <div className="qcp-section">
                                {loading ? (
                                    <div className="qcp-targets-skeleton" />
                                ) : (
                                    <div className="qcp-targets">
                                        {targets.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={`qcp-target${item.id === target.id ? " qcp-target--on" : ""}`}
                                                aria-pressed={item.id === target.id}
                                                onClick={() => setTargetId(item.id)}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {!loading && branches.length === 0 && <p className="qcp-hint">{t.qrCodes.noBranches}</p>}
                            </div>
                        )}

                        {/* --- The code --------------------------------------------------------- */}
                        <div className="qcp-preview">
                            <div className="qcp-paper">
                                <ReactQRCode
                                    ref={qrRef}
                                    value={url}
                                    size={PREVIEW_SIZE}
                                    level="M"
                                    marginSize={QUIET_ZONE_MODULES}
                                    background={QR_BACKGROUND}
                                    dataModulesSettings={{ color: QR_FOREGROUND, style: preset.dataModules }}
                                    finderPatternOuterSettings={{ color: QR_FOREGROUND, style: preset.finderOuter }}
                                    finderPatternInnerSettings={{ color: QR_FOREGROUND, style: preset.finderInner }}
                                />
                            </div>
                            <button type="button" className="qcp-link" onClick={handleCopy} title={t.qrCodes.copyAction}>
                                <span className="qcp-link-url" dir="ltr">
                                    {displayUrl}
                                </span>
                                <img
                                    src={copied ? "/icons/check-icon.svg" : "/icons/copy-icon.svg"}
                                    alt=""
                                    width={13}
                                    height={13}
                                    className="qcp-link-icon"
                                />
                            </button>
                        </div>

                        {/* --- Shape ------------------------------------------------------------ */}
                        <div className="qcp-section">
                            <div className="qcp-styles">
                                {stylePresets.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={`qcp-style${item.id === preset.id ? " qcp-style--on" : ""}`}
                                        aria-pressed={item.id === preset.id}
                                        onClick={() => setPresetId(item.id)}
                                        title={item.label}
                                    >
                                        <ReactQRCode
                                            value={SWATCH_VALUE}
                                            size={SWATCH_SIZE}
                                            level="L"
                                            marginSize={0}
                                            background={QR_BACKGROUND}
                                            dataModulesSettings={{ color: QR_FOREGROUND, style: item.dataModules }}
                                            finderPatternOuterSettings={{ color: QR_FOREGROUND, style: item.finderOuter }}
                                            finderPatternInnerSettings={{ color: QR_FOREGROUND, style: item.finderInner }}
                                        />
                                        <span className="qcp-style-label">{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* --- Download ------------------------------------------------------------- */}
                    <footer className="qcp-footer">
                        <div className="qcp-footer-head">
                            <img src="/icons/download-icon.svg" alt="" width={14} height={14} />
                            <p className="qcp-footer-label">{t.qrCodes.downloadLabel}</p>
                        </div>
                        <div className="qcp-formats">
                            {DOWNLOAD_FORMATS.map((format) => (
                                <button key={format.id} type="button" className="qcp-format" onClick={() => handleDownload(format.id)}>
                                    {format.label}
                                </button>
                            ))}
                        </div>
                        <p className="qcp-hint">{t.qrCodes.formatHint}</p>
                    </footer>
                </>
            )}
        </div>
    );
}
